// Recording Room — day-0.1 rev 2 (Ryan-direct feedback 2026-06-07 PM).
//
// Ryan's verbatim shape:
//   "I just want to hit record, and stop record. End to end. That's it.
//    And in the middle is the full recording. It should either scroll the
//    script or jump to the next line, upon hearing me end the last."
//
// Concretely:
//   - ALL cues across ALL scenes load (not just the current scene).
//   - RECORD = one big MediaRecorder session for the whole take. Hit Rec,
//     hit Stop, get one Blob. The script auto-scrolls + auto-advances the
//     "current cue" pointer on voice-activity-end (silence detect ~600ms
//     after sustained audio).
//   - RUN = browser SpeechSynthesis (TTS) reads the partner cues out loud
//     in a male voice; on YOUR cues the app stays quiet so you can say
//     your line aloud, then auto-advances when you stop speaking (silence
//     detect; falls back to a Tap-to-Advance affordance if you'd rather
//     drive manually).
//   - Fixed transport bar at the bottom — never reflows. Cue list scrolls
//     above it.
//   - Lock-screen transport via Media Session API stays on.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useArc } from "../state/arc";

type Mode = "RECORD" | "RUN";

interface LineCue {
  id: number;
  character: string | null;
  text: string;
  sceneIdx: number;
  sceneName: string;
  isYou: boolean;
  isAction: boolean;
}

const SILENCE_THRESHOLD = 0.018;     // amplitude floor; tweak with mic gain
const SILENCE_HOLD_MS    = 700;       // how long below threshold = "done"
const VOICE_ARMING_MS    = 220;       // require this much voice before
                                      // we'll consider the next silence as
                                      // end-of-line (prevents instant advance)

export default function RecordingRoom() {
  const navigate = useNavigate();
  const { state } = useArc();
  const script = state.parsedScript;
  const character = state.selectedCharacter;

  const [mode, setMode] = useState<Mode>("RECORD");
  const [cueIdx, setCueIdx] = useState(0);
  const [recording, setRecording] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [timerS, setTimerS] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(Array(40).fill(0));
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [takeUrl, setTakeUrl] = useState<string | null>(null);

  // Refs for the audio plumbing — avoid stale-closure issues
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const playerRef = useRef<HTMLAudioElement | null>(null);
  const ttsRef = useRef<SpeechSynthesisUtterance | null>(null);
  const cueIdxRef = useRef(0);
  cueIdxRef.current = cueIdx;
  const recordingRef = useRef(false);
  recordingRef.current = recording;
  const playingRef = useRef(false);
  playingRef.current = playing;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const voiceTimerRef = useRef<{ voiceStartMs: number | null; silenceStartMs: number | null }>({
    voiceStartMs: null,
    silenceStartMs: null,
  });
  const cuesRef = useRef<LineCue[]>([]);

  const cueListRef = useRef<HTMLDivElement | null>(null);

  // ----- flatten ALL cues across ALL scenes (Ryan: "way more lines than 34") -----
  const allCues: LineCue[] = useMemo(() => {
    if (!script) return [];
    const out: LineCue[] = [];
    for (const sc of script.scenes) {
      for (const lid of sc.line_ids) {
        const ln = script.lines.find((l) => l.id === lid);
        if (!ln || !ln.text?.trim()) continue;
        out.push({
          id: ln.id,
          character: ln.character,
          text: ln.text,
          sceneIdx: sc.order_index,
          sceneName: sc.name,
          isYou: ln.character === character,
          isAction: !ln.character,
        });
      }
    }
    return out;
  }, [script, character]);
  cuesRef.current = allCues;

  const cuesByScene = useMemo(() => {
    const m = new Map<number, { name: string; cues: LineCue[] }>();
    for (const c of allCues) {
      if (!m.has(c.sceneIdx)) m.set(c.sceneIdx, { name: c.sceneName, cues: [] });
      m.get(c.sceneIdx)!.cues.push(c);
    }
    return m;
  }, [allCues]);

  const currentCue: LineCue | undefined = allCues[cueIdx];

  // ----- scroll the active cue into view -----
  useEffect(() => {
    const el = document.querySelector(`[data-cue-id="${currentCue?.id ?? ""}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [cueIdx, currentCue?.id]);

  // ----- Media Session API -----
  useEffect(() => {
    if (!("mediaSession" in navigator) || !script || !currentCue) return;
    const ms: any = navigator.mediaSession;
    try {
      ms.metadata = new (window as any).MediaMetadata({
        title:  `${currentCue.character ?? "—"}`,
        artist: character ?? "Off Book",
        album:  script.title,
      });
    } catch { /* MediaMetadata may be unavailable */ }
    const advance = () => setCueIdx((i) => Math.min(allCues.length - 1, i + 1));
    const rewind  = () => setCueIdx((i) => Math.max(0, i - 1));
    try {
      ms.setActionHandler("play",          () => playFromHere());
      ms.setActionHandler("pause",         () => stopAll());
      ms.setActionHandler("previoustrack", rewind);
      ms.setActionHandler("nexttrack",     advance);
    } catch { /* no-op */ }
    return () => {
      try {
        ms.setActionHandler("play", null);
        ms.setActionHandler("pause", null);
        ms.setActionHandler("previoustrack", null);
        ms.setActionHandler("nexttrack", null);
      } catch { /* no-op */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script, character, currentCue?.id, allCues.length]);

  // ----- record timer -----
  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => setTimerS((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [recording]);

  // ----- voice-activity detection loop (shared by RECORD + RUN) -----
  function startAnalyserLoop() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (!analyserRef.current) return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      const bands = 40;
      const step = Math.floor(buf.length / bands);
      const samples: number[] = [];
      let amp = 0;
      for (let i = 0; i < bands; i++) {
        let max = 0;
        for (let j = 0; j < step; j++) {
          const v = Math.abs(buf[i * step + j] - 128) / 128;
          if (v > max) max = v;
        }
        samples.push(Math.min(1, max * 1.4));
        amp += max;
      }
      amp /= bands;
      sum = amp;
      setWaveform(samples);

      // Voice activity / silence tracking
      const now = performance.now();
      const vt = voiceTimerRef.current;
      if (sum > SILENCE_THRESHOLD) {
        // voice present
        if (vt.voiceStartMs === null) vt.voiceStartMs = now;
        vt.silenceStartMs = null;
      } else {
        // silence
        if (vt.silenceStartMs === null) vt.silenceStartMs = now;
        if (autoAdvanceRef.current &&
            vt.voiceStartMs !== null &&
            (now - vt.voiceStartMs) >= VOICE_ARMING_MS &&
            (now - vt.silenceStartMs) >= SILENCE_HOLD_MS) {
          // armed voice + held silence = end of line. Advance ONLY if the
          // current cue is one the actor is supposed to be speaking.
          const cur = cuesRef.current[cueIdxRef.current];
          if (cur && shouldUserSpeak(cur)) {
            vt.voiceStartMs = null;
            vt.silenceStartMs = null;
            advanceCue();
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  // Keep autoAdvance accessible from the rAF closure
  const autoAdvanceRef = useRef(autoAdvance);
  autoAdvanceRef.current = autoAdvance;

  function shouldUserSpeak(c: LineCue): boolean {
    // In RECORD, EVERY non-action cue is the actor's voice (they read the
    // whole script aloud, partner lines included, for a complete take).
    if (modeRef.current === "RECORD") return !c.isAction;
    // In RUN, only the actor's own lines.
    return c.isYou && !c.isAction;
  }

  function advanceCue() {
    setCueIdx((i) => {
      const next = i + 1;
      if (next >= cuesRef.current.length) {
        // End of script.
        if (recordingRef.current) stopRecording();
        if (playingRef.current) stopAll();
        return i;
      }
      // RUN mode: if the upcoming cue is partner, start TTS reading it.
      if (modeRef.current === "RUN") {
        const next_c = cuesRef.current[next];
        if (next_c && !shouldUserSpeak(next_c) && !next_c.isAction) {
          speakPartnerCue(next_c);
        }
      }
      return next;
    });
  }

  // ----- RECORD lifecycle -----
  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const rec = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => e.data && chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setTakeUrl(url);
        stream.getTracks().forEach((t) => t.stop());
        try { ctx.close(); } catch { /* no-op */ }
        audioCtxRef.current = null;
        analyserRef.current = null;
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      setTimerS(0);
      setCueIdx(0);
      startAnalyserLoop();
    } catch (e) {
      console.error("mic init failed", e);
      alert("Couldn't access the mic. Check permissions.");
    }
  }

  function stopRecording() {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
    setRecording(false);
  }

  // ----- RUN lifecycle (TTS partner reads + silence-advance on actor) -----
  function pickTtsVoice(genderHint: "male" | "neutral" = "male"): SpeechSynthesisVoice | null {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const want = (v: SpeechSynthesisVoice) =>
      v.lang.startsWith("en") &&
      (genderHint === "male"
        ? /(male|Daniel|David|Alex|Fred|Aaron|Rishi|Arthur|Oliver)/i.test(v.name)
        : true);
    return voices.find(want) || voices.find((v) => v.lang.startsWith("en")) || voices[0];
  }

  function speakPartnerCue(c: LineCue) {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(c.text);
    const v = pickTtsVoice("male");
    if (v) u.voice = v;
    u.rate = 1.0;
    u.pitch = 0.95;
    u.onend = () => {
      // After partner finishes, auto-advance to the actor's line (which the
      // analyser loop will then watch for via silence-advance).
      if (modeRef.current === "RUN" && playingRef.current) advanceCue();
    };
    ttsRef.current = u;
    window.speechSynthesis.speak(u);
  }

  async function playFromHere() {
    // RUN with TTS for partner; opens the mic so silence-advance works on
    // the actor's cues. No recording — just analyser.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      startAnalyserLoop();
      setPlaying(true);

      // Begin: if current cue is partner, speak it; otherwise just wait for
      // the actor.
      const cur = cuesRef.current[cueIdxRef.current];
      if (cur && !cur.isAction && !shouldUserSpeak(cur)) speakPartnerCue(cur);
    } catch (e) {
      console.error("mic init for run failed", e);
      alert("Couldn't access the mic for Run mode.");
    }
  }

  function stopAll() {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { window.speechSynthesis.cancel(); } catch { /* no-op */ }
    try { audioCtxRef.current?.close(); } catch { /* no-op */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    try { playerRef.current?.pause(); } catch { /* no-op */ }
    setPlaying(false);
  }

  // Play back the take (recording playback) — alternate RUN sub-action
  function playRecordedTake() {
    if (!takeUrl) return;
    const audio = new Audio(takeUrl);
    playerRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.play().then(() => setPlaying(true)).catch(() => { /* silent */ });
  }

  // ----- Render -----
  if (!script || !allCues.length) {
    return (
      <div className="v2">
        <h2>No script loaded.</h2>
        <button className="v2-affordance" onClick={() => navigate("/script")}>← Script</button>
      </div>
    );
  }

  const partnerLabel = (c: LineCue) =>
    c.isAction ? "—" : (c.isYou ? `${c.character} · YOU` : c.character || "");

  // Layout: scrollable cue list at top, fixed transport at bottom.
  return (
    <div className="v2" style={{ paddingBottom: "14rem" }}>
      {/* Header */}
      <p className="v2-tracked-caps" style={{ textAlign: "center" }}>{script.title}</p>
      <p className="v2-tracked-caps" style={{ textAlign: "center", marginTop: "0.2rem" }}>
        Playing {character}
      </p>

      {/* Mode switcher — gold hairline on selected */}
      <div style={{ display: "flex", justifyContent: "center", gap: "3rem", margin: "1.2rem 0" }}>
        <button
          className={"v2-affordance" + (mode === "RECORD" ? " is-selected" : "")}
          onClick={() => { stopAll(); setMode("RECORD"); }}
          disabled={recording || playing}
        >
          Record
        </button>
        <button
          className={"v2-affordance" + (mode === "RUN" ? " is-selected" : "")}
          onClick={() => { stopAll(); setMode("RUN"); }}
          disabled={recording || playing}
        >
          Run
        </button>
      </div>

      {/* Scrolling cue list — every cue across every scene */}
      <div ref={cueListRef} className="v2-paper" style={{ paddingBottom: "1rem" }}>
        {Array.from(cuesByScene.entries()).map(([sidx, { name, cues }]) => (
          <section key={sidx} style={{ marginBottom: "1.5rem" }}>
            <p className="v2-tracked-caps">{name}</p>
            {cues.map((c) => {
              const isCurrent = c.id === currentCue?.id;
              if (c.isAction) {
                return (
                  <p key={c.id} data-cue-id={c.id} className="v2-stage">{c.text}</p>
                );
              }
              return (
                <div key={c.id} data-cue-id={c.id} style={{ opacity: isCurrent ? 1 : c.isYou ? 0.85 : 0.5, marginBottom: "0.2rem" }}>
                  <p className="v2-cue-label" style={{ marginBottom: 0 }}>{partnerLabel(c)}</p>
                  <p className="v2-cue-text" style={{
                    fontSize: isCurrent ? "1.2rem" : "1rem",
                    fontStyle: c.isYou ? "italic" : "normal",
                    color: isCurrent ? "var(--v2-ink)" : c.isYou ? "var(--v2-ink)" : "var(--v2-ink-soft)",
                  }}>
                    {c.text}
                  </p>
                </div>
              );
            })}
          </section>
        ))}
      </div>

      {/* FIXED transport bar — above the bottom NavChrome */}
      <div style={{
        position: "fixed",
        left: 0, right: 0, bottom: "4.2rem",
        background: "var(--v2-cream)",
        borderTop: "1px solid var(--v2-hairline)",
        padding: "0.9rem 1rem 1rem",
        zIndex: 26,
      }}>
        {/* Live waveform (always, both modes) */}
        <div className="v2-waveform" style={{ height: "44px", margin: "0 0 0.4rem" }} aria-hidden>
          {waveform.map((h, i) => (
            <div key={i} className="v2-wave-bar" style={{ height: `${Math.max(2, h * 40)}px` }} />
          ))}
        </div>

        {/* Cue counter */}
        <p className="v2-tracked-caps" style={{ textAlign: "center", margin: "0 0 0.5rem" }}>
          {Math.min(cueIdx + 1, allCues.length)} / {allCues.length}
          {recording && ` · REC ${Math.floor(timerS/60)}:${String(timerS%60).padStart(2,"0")}`}
        </p>

        <div className="v2-transport" style={{ margin: 0 }}>
          <button
            className="v2-transport-btn"
            onClick={() => setCueIdx((i) => Math.max(0, i - 1))}
            disabled={recording || playing}
          >
            Prev
          </button>
          {mode === "RECORD" ? (
            <button
              className={"v2-transport-primary" + (recording ? " is-recording" : "")}
              onClick={recording ? stopRecording : startRecording}
              aria-label={recording ? "Stop recording" : "Start recording"}
            >
              {recording ? "Stop" : "Rec"}
            </button>
          ) : (
            <button
              className={"v2-transport-primary" + (playing ? " is-playing" : "")}
              onClick={playing ? stopAll : playFromHere}
              aria-label={playing ? "Stop run" : "Start run"}
            >
              {playing ? "Stop" : "Run"}
            </button>
          )}
          <button
            className="v2-transport-btn"
            onClick={() => setCueIdx((i) => Math.min(allCues.length - 1, i + 1))}
            disabled={recording || playing}
          >
            Next
          </button>
        </div>

        {/* Sub-affordances */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem", padding: "0 0.4rem" }}>
          <button
            className="v2-affordance" style={{ fontSize: "0.65rem", padding: "0.3rem 0" }}
            onClick={() => setAutoAdvance((v) => !v)}
          >
            {autoAdvance ? "auto-advance on" : "auto-advance off"}
          </button>
          {takeUrl && mode === "RECORD" && !recording && (
            <button className="v2-affordance" style={{ fontSize: "0.65rem", padding: "0.3rem 0" }} onClick={playRecordedTake}>
              Hear the take
            </button>
          )}
          <button
            className="v2-affordance" style={{ fontSize: "0.65rem", padding: "0.3rem 0" }}
            onClick={() => { stopAll(); navigate("/script"); }}
            disabled={recording || playing}
          >
            Script
          </button>
        </div>
      </div>
    </div>
  );
}
